import _get from 'lodash/get';
import _flatten from 'lodash/flatten';
import { PERSON_ENTITY_TYPE_KEY } from 'helpers/useEntityTypes';

export default function getProfileAttributes(typeKey, entityType, client) {
  const webProfileLayoutKey = typeKey === PERSON_ENTITY_TYPE_KEY ? 'web_profile' : 'profile-info';
  const webProfileLayout = _get(entityType, `_layouts.${webProfileLayoutKey}`, []);
  const profilePeopleLayout = _get(entityType, '_layouts.profile-people', []);
  const orgLevelsLayout = _get(entityType, '_layouts.org-levels', []);

  function findField(name) {
    return _get(entityType, `_fields[${name}]`, {});
  }

  function findCategory(name) {
    const categoryKeys = Object.keys(entityType._categories);
    const categoryName = categoryKeys.find(field => field === name);
    return _get(entityType, `_categories[${categoryName}]`, {});
  }

  function buildCategory(value, shouldBuildCategory = true) {
    const foundCategory = findCategory(value.name);
    const categories = [];
    if (shouldBuildCategory) categories.push(foundCategory);

    if (foundCategory && foundCategory.children) {
      foundCategory.children.forEach(child => {
        if (child.type === 'category') {
          buildCategory(child);
        } else if (child.type === 'field') {
          categories.push(buildField(child));
        }
      });
      return categories;
    }
  }

  function buildField(value) {
    return findField(value.name);
  }

  function buildBasicLayoutObject(layout, shouldBuildCategory = true) {
    const newBasicLayout = [];
    let returnValue;

    layout &&
      layout.forEach(value => {
        if (value.type === 'category') {
          returnValue = buildCategory(value, shouldBuildCategory);
        } else if (value.type === 'field') {
          returnValue = buildField(value);
        }
        newBasicLayout.push(returnValue);
      });
    return _flatten(newBasicLayout);
  }

  const flattenedWebProfile = buildBasicLayoutObject(webProfileLayout, false);
  const flattenedProfilePeople = buildBasicLayoutObject(profilePeopleLayout);
  const flattenedOrgLevels = buildBasicLayoutObject(orgLevelsLayout);
  let headerItems =
    typeKey === PERSON_ENTITY_TYPE_KEY
      ? [
          findField('firstName'),
          findField('lastName'),
          findField('title'),
          findField('anniversaryDate'),
        ]
      : [];
  const namePronunciation = _get(client, 'settings.namePronunciation.enabled', false);
  const timezoneEnabled = _get(client, 'settings.timezoneAndWorkHours.timezone.enabled', false);

  if (timezoneEnabled) {
    // we only need to add `timezoneAndWorkHours` (and not `timezoneAndWorkHours.timezone`
    // or `timezoneAndWorkHours.workHoursItem`) because:
    // - We only use this helper within Attributes/container.js and AttributesUsage.tsx (in
    //      the "on profile" column of the attributes table and the attribute editing modal)
    //      to show the user whether an attribute is "on profile" or not
    // - We don't use AttributesUsage.tsx for non-user-entity-type attributes:
    //    - As the parent field (timezoneAndWorkHours) will never be added to other entity
    //      types besides `user`, and we only render `AttributesUsage` for user entity-type
    //      attributes,
    // - Attributes/container.js filters out fields that don't have fields defined or item.type
    // defined
    //    - `timezoneAndWorkHours.timezone` and `timezoneAndWorkHours.workHoursItem`
    //      do not have fields or item.type
    headerItems.push(findField('timezoneAndWorkHours'));
  }

  if (namePronunciation) {
    headerItems.push(findField('hasNamePronunciation'));
  }

  const profileAttributes = [
    ...flattenedWebProfile,
    ...flattenedProfilePeople,
    ...flattenedOrgLevels,
    ...headerItems,
  ];
  return profileAttributes.map(attribute => attribute.objectKey);
}
